/**
 * Utility functions for determining geographic areas in Perth, WA
 */

export type AreaType = 'north' | 'south' | 'western' | 'eastern' | 'regional';

/**
 * Determine the area of Perth based on GPS coordinates
 *
 * Perth River divides North/South roughly at latitude -31.95
 * Western suburbs are west of longitude 115.85
 * Eastern suburbs are east of longitude 116.05 (includes Lesmurdie, Kalamunda, etc.)
 * Regional is outside the greater Perth metro area
 */
export function getAreaFromCoordinates(lat: number | null | undefined, lng: number | null | undefined): AreaType | null {
  if (!lat || !lng) return null;

  // Define Perth metro boundaries (approximate)
  const PERTH_METRO_BOUNDS = {
    north: -31.6,    // Yanchep area
    south: -32.3,    // Mandurah area
    west: 115.65,    // Ocean
    east: 116.08,    // Extended to include Lesmurdie, Kalamunda, Gooseberry Hill
  };

  // Check if location is outside Perth metro (regional)
  if (
    lat < PERTH_METRO_BOUNDS.south ||
    lat > PERTH_METRO_BOUNDS.north ||
    lng < PERTH_METRO_BOUNDS.west ||
    lng > PERTH_METRO_BOUNDS.east
  ) {
    return 'regional';
  }

  // Swan River roughly divides north/south at latitude -31.95
  const RIVER_LATITUDE = -31.95;

  // Western suburbs boundary (roughly Stirling Highway / West Coast Highway)
  const WESTERN_SUBURBS_LONGITUDE = 115.85;

  // Eastern suburbs boundary (includes Perth Hills - Lesmurdie, Kalamunda, Midland, etc.)
  const EASTERN_SUBURBS_LONGITUDE = 116.0;

  // Determine if Western suburbs (coastal areas like Cottesloe, Claremont, etc.)
  if (lng < WESTERN_SUBURBS_LONGITUDE) {
    return 'western';
  }

  // Determine if Eastern suburbs (hills areas like Kalamunda, Midland, etc.)
  if (lng > EASTERN_SUBURBS_LONGITUDE) {
    return 'eastern';
  }

  // Otherwise, determine North or South of the river
  if (lat > RIVER_LATITUDE) {
    return 'north';
  } else {
    return 'south';
  }
}

/**
 * Get human-readable area name
 */
export function getAreaLabel(area: AreaType | null): string {
  const labels: Record<AreaType, string> = {
    north: 'North of the River',
    south: 'South of the River',
    western: 'Western Suburbs',
    eastern: 'Eastern Suburbs',
    regional: 'Regional',
  };

  return area ? labels[area] : '';
}

/**
 * Check if a location matches any of the selected areas
 */
export function matchesAreas(
  lat: number | null | undefined,
  lng: number | null | undefined,
  selectedAreas: string[]
): boolean {
  if (selectedAreas.length === 0) return true;
  
  const area = getAreaFromCoordinates(lat, lng);
  return area ? selectedAreas.includes(area) : false;
}

